// Assets — the fixed asset register: what Rootegra owns, what it cost, how
// much has been depreciated, and the monthly depreciation run.
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBooksAccess } from '../../contexts/AuthContext';
import { Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { day, dollars, monthName } from '../../lib/format';
import { assets as assetsApi } from '../../lib/ledger/api';
import type { Account, AssetInput, DepreciationRun, FixedAsset } from '../../lib/ledger/types';
import { AccountSelect, BackTo, Money, MoneyInput, ReadOnlyNote, downloadCsv, today, useAccounts } from './shared';

function useLoad<T>(fn: () => Promise<T>, deps: readonly unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}

type Category = FixedAsset['category'];
const CATEGORY_WORDS: Record<Category, string> = { equipment: 'Equipment', furniture: 'Furniture', software: 'Software', other: 'Other' };
const DEFAULT_LIFE: Record<Category, number> = { equipment: 60, furniture: 84, software: 36, other: 60 };
const SOURCE_WORDS: Record<FixedAsset['source'], string> = { purchase: 'Purchased', contribution: 'Contributed by a member', opening: 'Opening balance' };
const MEMBER_WORDS: Record<string, string> = { leah: 'Leah', laura: 'Laura' };

function monthlyAmount(a: FixedAsset): number {
  return Math.round((Number(a.cost_cents) - Number(a.salvage_cents ?? 0)) / Math.max(1, Number(a.life_months)));
}

function statusOf(a: FixedAsset, accum: number): { words: string; tone: '' | 'lp-tag--good' | 'lp-tag--warn' } {
  if (a.disposed_on) return { words: 'Disposed', tone: '' };
  if (accum >= Number(a.cost_cents) - Number(a.salvage_cents ?? 0)) return { words: 'Fully depreciated', tone: 'lp-tag--warn' };
  return { words: 'In service', tone: 'lp-tag--good' };
}

function lastMonthKey(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function cashCandidates(accounts: Account[]): Account[] {
  return accounts.filter((a) => (a.type === 'asset' && (a.subtype === 'bank' || a.subtype === 'vessel')) || (a.type === 'liability' && (a.subtype === 'credit_card' || a.subtype === 'due_to_member')));
}

export function AssetsTab() {
  const { canEdit } = useBooksAccess();
  const chart = useAccounts();
  const [params, setParams] = useSearchParams();
  const list = useLoad(() => assetsApi.list(), []);
  const runs = useLoad(() => assetsApi.runs(), []);
  const reload = () => { list.reload(); runs.reload(); };

  const accumById = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of runs.data ?? []) m.set(r.asset_id, (m.get(r.asset_id) ?? 0) + Number(r.amount_cents));
    return m;
  }, [runs.data]);

  const rows = list.data ?? [];
  const openId = params.get('asset');
  const open = openId ? rows.find((a) => a.id === openId) ?? null : null;
  const show = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('asset', id); else next.delete('asset');
    setParams(next);
  };

  if (openId && list.loading) return <Empty>Loading…</Empty>;
  if (open) {
    return (
      <AssetDetail
        asset={open}
        runs={(runs.data ?? []).filter((r) => r.asset_id === open.id)}
        accounts={chart.accounts}
        byId={chart.byId}
        canEdit={canEdit}
        onBack={() => show(null)}
        onChanged={reload}
      />
    );
  }

  const exportCsv = () => {
    downloadCsv('rootegra-fixed-assets.csv', [
      ['Name', 'Category', 'Source', 'Acquired', 'In service', 'Cost', 'Tax basis', 'Salvage', 'Life (months)', 'Monthly', 'Accumulated depreciation', 'Book value', 'Status', 'Disposed on'],
      ...rows.map((a) => {
        const accum = accumById.get(a.id) ?? 0;
        return [a.name, CATEGORY_WORDS[a.category], SOURCE_WORDS[a.source], a.acquired_on, a.in_service_on, (Number(a.cost_cents) / 100).toFixed(2), a.tax_basis_cents == null ? '' : (Number(a.tax_basis_cents) / 100).toFixed(2), (Number(a.salvage_cents ?? 0) / 100).toFixed(2), a.life_months, (monthlyAmount(a) / 100).toFixed(2), (accum / 100).toFixed(2), ((Number(a.cost_cents) - accum) / 100).toFixed(2), statusOf(a, accum).words, a.disposed_on ?? ''];
      }),
    ]);
  };

  return (
    <>
      {!canEdit && <ReadOnlyNote />}
      <ErrorNote error={chart.error} />
      <Section title="Register" right={<button type="button" className="lp-btn lp-btn--quiet lp-no-print" disabled={rows.length === 0} onClick={exportCsv}>CSV</button>}>
        <ErrorNote error={list.error} />
        <ErrorNote error={runs.error} />
        {openId && !open && !list.loading && <Note tone="warn">That asset is not in the register.</Note>}
        {list.loading ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No fixed assets yet. Equipment, furniture and software that last more than a year belong here.</Empty> : (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead>
                <tr><th>Asset</th><th>Category</th><th>In service</th><th className="num">Cost</th><th className="num">Tax basis</th><th className="num">Accumulated</th><th className="num">Book value</th><th className="num">Monthly</th><th>Status</th></tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const accum = accumById.get(a.id) ?? 0;
                  const st = statusOf(a, accum);
                  return (
                    <tr key={a.id} className={a.disposed_on ? 'quiet' : undefined}>
                      <td>
                        <button type="button" className="lp-btn lp-btn--quiet lp-btn--small" onClick={() => show(a.id)} style={{ textAlign: 'left' }}>{a.name}</button>
                        {a.source === 'contribution' && <div className="muted">Contributed by {MEMBER_WORDS[a.contributed_by ?? ''] ?? 'a member'}</div>}
                      </td>
                      <td>{CATEGORY_WORDS[a.category]}</td>
                      <td>{day(a.in_service_on)}</td>
                      <td className="num"><Money cents={a.cost_cents} /></td>
                      <td className="num">{a.tax_basis_cents == null ? <span className="muted">same</span> : <Money cents={a.tax_basis_cents} />}</td>
                      <td className="num"><Money cents={accum} blankZero /></td>
                      <td className="num"><Money cents={a.disposed_on ? 0 : Number(a.cost_cents) - accum} /></td>
                      <td className="num"><Money cents={a.disposed_on ? 0 : monthlyAmount(a)} blankZero /></td>
                      <td><span className={`lp-tag ${st.tone}`}>{st.words}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted">Property a member brings in goes under Members → Record a contribution; that creates the asset here with the member's basis.</p>
      </Section>

      <RunDepreciation canEdit={canEdit} onRan={reload} />
      <AddAsset accounts={chart.accounts} canEdit={canEdit} onAdded={reload} />

      <Note>
        Contributed property keeps the member's tax basis for tax depreciation and is not eligible for Section 179 or bonus depreciation. These books carry straight-line book depreciation; the accountant handles the tax side.
      </Note>
    </>
  );
}

function RunDepreciation({ canEdit, onRan }: { canEdit: boolean; onRan: () => void }) {
  const [month, setMonth] = useState(lastMonthKey());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  async function run() {
    setBusy(true); setErr(null); setDone(null);
    try {
      const r = await assetsApi.runDepreciation(`${month}-01`);
      setDone(r.posted === 0
        ? `Nothing to post for ${monthName(`${month}-01`)}: every asset is either not yet in service, already run, fully depreciated or disposed.`
        : `Posted depreciation for ${r.posted} ${r.posted === 1 ? 'asset' : 'assets'} in ${monthName(`${month}-01`)}, ${dollars(r.total_cents)} in total.`);
      onRan();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }

  return (
    <Section title="Run depreciation">
      <p className="muted">One straight-line entry per asset for the month: cost less salvage, spread over its life, with the last month taking the remainder. Running a month twice does nothing.</p>
      <ErrorNote error={err} />
      {done && <Note tone="good">{done}</Note>}
      <div className="lp-row">
        <input className="lp-inp" type="month" value={month} onChange={(e) => setMonth(e.target.value)} disabled={!canEdit} style={{ maxWidth: 200 }} />
        <button type="button" className="lp-btn lp-btn--primary" disabled={!canEdit || busy || !month} onClick={() => void run()}>{busy ? 'Posting…' : 'Run depreciation'}</button>
      </div>
    </Section>
  );
}

type AddDraft = {
  source: 'purchase' | 'opening';
  name: string;
  description: string;
  category: Category;
  acquired_on: string;
  in_service_on: string;
  cost: number | null;
  tax_basis: number | null;
  salvage: number | null;
  life_months: string;
  lifeTouched: boolean;
  paid_from_account_id: string;
};

function AddAsset({ accounts, canEdit, onAdded }: { accounts: Account[]; canEdit: boolean; onAdded: () => void }) {
  const [openForm, setOpenForm] = useState(false);
  const fresh = (): AddDraft => ({ source: 'purchase', name: '', description: '', category: 'equipment', acquired_on: today(), in_service_on: today(), cost: null, tax_basis: null, salvage: 0, life_months: String(DEFAULT_LIFE.equipment), lifeTouched: false, paid_from_account_id: '' });
  const [d, setD] = useState<AddDraft>(fresh);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  const life = Number(d.life_months);
  const ready = d.name.trim() && d.acquired_on && d.in_service_on && d.cost != null && d.cost > 0 && Number.isInteger(life) && life > 0 && (d.source !== 'purchase' || d.paid_from_account_id);

  async function add() {
    if (!ready || d.cost == null) return;
    setBusy(true); setErr(null); setDone(null);
    try {
      const input: AssetInput = {
        name: d.name.trim(), description: d.description.trim() || null, category: d.category, acquired_on: d.acquired_on, in_service_on: d.in_service_on,
        cost_cents: d.cost, tax_basis_cents: d.tax_basis, salvage_cents: d.salvage ?? 0, life_months: life, source: d.source,
        paid_from_account_id: d.source === 'purchase' ? d.paid_from_account_id : null,
      };
      await assetsApi.add(input);
      setDone(`Added ${d.name.trim()} at ${dollars(d.cost)}.`);
      setD(fresh());
      setOpenForm(false);
      onAdded();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }

  return (
    <Section title="Add an asset" right={<button type="button" className="lp-btn lp-btn--quiet lp-no-print" disabled={!canEdit} onClick={() => setOpenForm((v) => !v)}>{openForm ? 'Close' : 'Add an asset'}</button>}>
      <ErrorNote error={err} />
      {done && <Note tone="good">{done}</Note>}
      {!openForm && !done && <p className="muted">A purchase posts the asset against the account it was paid from. An opening balance brings in something Rootegra already owned when these books started.</p>}
      {openForm && (
        <div className="lp-form">
          <div className="lp-form-grid">
            <Field label="How it came in">
              <select className="lp-inp" value={d.source} disabled={!canEdit} onChange={(e) => setD({ ...d, source: e.target.value as AddDraft['source'] })}>
                <option value="purchase">Purchase</option>
                <option value="opening">Opening balance</option>
              </select>
            </Field>
            <Field label="Name"><input className="lp-inp" value={d.name} disabled={!canEdit} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="MacBook Pro 14" /></Field>
            <Field label="Description"><input className="lp-inp" value={d.description} disabled={!canEdit} onChange={(e) => setD({ ...d, description: e.target.value })} placeholder="Serial, model, where it lives" /></Field>
            <Field label="Category">
              <select
                className="lp-inp"
                value={d.category}
                disabled={!canEdit}
                onChange={(e) => {
                  const c = e.target.value as Category;
                  setD({ ...d, category: c, life_months: d.lifeTouched ? d.life_months : String(DEFAULT_LIFE[c]) });
                }}
              >
                {(Object.keys(CATEGORY_WORDS) as Category[]).map((c) => <option key={c} value={c}>{CATEGORY_WORDS[c]}</option>)}
              </select>
            </Field>
            <Field label="Acquired"><input className="lp-inp" type="date" value={d.acquired_on} disabled={!canEdit} onChange={(e) => setD({ ...d, acquired_on: e.target.value, in_service_on: d.in_service_on === d.acquired_on ? e.target.value : d.in_service_on })} /></Field>
            <Field label="In service" hint="Depreciation starts this month."><input className="lp-inp" type="date" value={d.in_service_on} disabled={!canEdit} onChange={(e) => setD({ ...d, in_service_on: e.target.value })} /></Field>
            <Field label="Cost"><MoneyInput cents={d.cost} onChange={(c) => setD({ ...d, cost: c })} disabled={!canEdit} /></Field>
            <Field label="Tax basis" hint="Leave blank when it matches cost."><MoneyInput cents={d.tax_basis} onChange={(c) => setD({ ...d, tax_basis: c })} disabled={!canEdit} placeholder="Same as cost" /></Field>
            <Field label="Salvage"><MoneyInput cents={d.salvage} onChange={(c) => setD({ ...d, salvage: c })} disabled={!canEdit} /></Field>
            <Field label="Life in months" hint="60 equipment · 84 furniture · 36 software">
              <input className="lp-inp lp-money" inputMode="numeric" value={d.life_months} disabled={!canEdit} onChange={(e) => setD({ ...d, life_months: e.target.value.replace(/\D/g, ''), lifeTouched: true })} />
            </Field>
            {d.source === 'purchase' && (
              <Field label="Paid from"><AccountSelect value={d.paid_from_account_id} onChange={(id) => setD({ ...d, paid_from_account_id: id })} accounts={cashCandidates(accounts)} disabled={!canEdit} /></Field>
            )}
          </div>
          <div className="lp-row">
            <button type="button" className="lp-btn lp-btn--primary" disabled={!canEdit || busy || !ready} onClick={() => void add()}>{busy ? 'Adding…' : 'Add asset'}</button>
            <button type="button" className="lp-btn lp-btn--quiet" disabled={busy} onClick={() => { setOpenForm(false); setD(fresh()); }}>Cancel</button>
          </div>
        </div>
      )}
    </Section>
  );
}

function AssetDetail({ asset: a, runs, accounts, byId, canEdit, onBack, onChanged }: {
  asset: FixedAsset;
  runs: DepreciationRun[];
  accounts: Account[];
  byId: Map<string, Account>;
  canEdit: boolean;
  onBack: () => void;
  onChanged: () => void;
}) {
  const accum = runs.reduce((s, r) => s + Number(r.amount_cents), 0);
  const st = statusOf(a, accum);
  const [date, setDate] = useState(today());
  const [proceeds, setProceeds] = useState<number | null>(0);
  const [toAccount, setToAccount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  async function dispose() {
    if (!toAccount) return;
    setBusy(true); setErr(null); setDone(null);
    try {
      await assetsApi.dispose(a.id, date, proceeds ?? 0, toAccount, note.trim());
      setDone(`Disposed of ${a.name} on ${day(date)}.`);
      onChanged();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }

  const acct = (id: string) => { const x = byId.get(id); return x ? `${x.code} · ${x.name}` : '—'; };
  let running = 0;

  return (
    <>
      <BackTo label="Assets" onClick={onBack} />
      {!canEdit && <ReadOnlyNote />}
      <Section title={a.name}>
        <dl className="lp-kv">
          <dt>Status</dt><dd><span className={`lp-tag ${st.tone}`}>{st.words}</span></dd>
          <dt>Category</dt><dd>{CATEGORY_WORDS[a.category]}</dd>
          <dt>Came in as</dt><dd>{SOURCE_WORDS[a.source]}{a.contributed_by ? ` (${MEMBER_WORDS[a.contributed_by] ?? a.contributed_by})` : ''}</dd>
          {a.description && <><dt>Description</dt><dd>{a.description}</dd></>}
          <dt>Acquired</dt><dd>{day(a.acquired_on)}</dd>
          <dt>In service</dt><dd>{day(a.in_service_on)}</dd>
          <dt>Cost</dt><dd><Money cents={a.cost_cents} /></dd>
          <dt>Tax basis</dt><dd>{a.tax_basis_cents == null ? 'Same as cost' : <Money cents={a.tax_basis_cents} />}</dd>
          <dt>Salvage</dt><dd><Money cents={a.salvage_cents ?? 0} /></dd>
          <dt>Life</dt><dd>{a.life_months} months, straight line</dd>
          <dt>Monthly depreciation</dt><dd><Money cents={monthlyAmount(a)} /></dd>
          <dt>Accumulated depreciation</dt><dd><Money cents={accum} /></dd>
          <dt>Book value</dt><dd><Money cents={a.disposed_on ? 0 : Number(a.cost_cents) - accum} /></dd>
          <dt>Asset account</dt><dd>{acct(a.asset_account_id)}</dd>
          <dt>Accumulated depreciation account</dt><dd>{acct(a.accum_depr_account_id)}</dd>
          <dt>Depreciation expense account</dt><dd>{acct(a.depr_expense_account_id)}</dd>
          {a.consent_ref && <><dt>Consent</dt><dd>{a.consent_ref}</dd></>}
          {a.disposed_on && <><dt>Disposed</dt><dd>{day(a.disposed_on)}{a.disposal_note ? ` · ${a.disposal_note}` : ''}</dd></>}
        </dl>
      </Section>

      <Section title="Depreciation schedule">
        {runs.length === 0 ? <Empty>No depreciation posted yet. Run a month from the register.</Empty> : (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Month</th><th className="num">Amount</th><th className="num">Accumulated</th><th className="num">Book value after</th></tr></thead>
              <tbody>
                {runs.map((r) => {
                  running += Number(r.amount_cents);
                  return (
                    <tr key={r.id}>
                      <td>{monthName(r.month)}</td>
                      <td className="num"><Money cents={r.amount_cents} /></td>
                      <td className="num"><Money cents={running} /></td>
                      <td className="num"><Money cents={Number(a.cost_cents) - running} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Dispose">
        <ErrorNote error={err} />
        {done && <Note tone="good">{done}</Note>}
        {a.disposed_on ? <Empty>This asset was disposed of on {day(a.disposed_on)}.</Empty> : (
          <>
            <p className="muted">Removes the cost and its accumulated depreciation from the books, records what came in for it, and posts the difference as a gain or loss.</p>
            <div className="lp-form-grid">
              <Field label="Date"><input className="lp-inp" type="date" value={date} disabled={!canEdit} onChange={(e) => setDate(e.target.value)} /></Field>
              <Field label="Proceeds" hint="0 when it was scrapped or given away."><MoneyInput cents={proceeds} onChange={setProceeds} disabled={!canEdit} /></Field>
              <Field label="Proceeds went to"><AccountSelect value={toAccount} onChange={setToAccount} accounts={cashCandidates(accounts)} disabled={!canEdit} /></Field>
              <Field label="Note"><input className="lp-inp" value={note} disabled={!canEdit} onChange={(e) => setNote(e.target.value)} placeholder="Sold, traded in, no longer working" /></Field>
            </div>
            <div className="lp-row">
              <button type="button" className="lp-btn lp-btn--danger" disabled={!canEdit || busy || !toAccount || !date} onClick={() => void dispose()}>{busy ? 'Posting…' : 'Dispose of this asset'}</button>
            </div>
          </>
        )}
      </Section>
    </>
  );
}
